/* useCredits — the signed-in user's JAI-credit balance, kept fresh.
 *
 * One shared store for the whole app, so the Ask JAI header, Settings and the
 * out-of-credits dialog all show the same numbers from one request, not one
 * each. Source of truth is GET /api/billing/status (`credits`).
 *
 * Refreshes: on first use, after any change announced with
 * announceCreditsChanged() (services/api.js does this after a successful AI
 * call), and when the tab regains focus. Refreshes are debounced, because
 * several AI calls can finish together.
 *
 * Guests do not use credits, so for them this returns null and fetches nothing.
 */

import { useEffect, useSyncExternalStore } from 'react'
import { billingApi, CREDITS_CHANGED_EVENT } from '../services/api'
import { useAuth } from '../context/AuthContext'

let current = null
let owner = null // whose balance the store holds
let loaded = false
let inflight = null
let timer = null
const listeners = new Set()

const publish = (next) => {
  current = next
  loaded = true
  listeners.forEach((fn) => fn())
}

export function refreshCredits() {
  if (inflight) return inflight
  inflight = billingApi.getStatus()
    .then(({ data }) => publish(data?.credits || null))
    .catch(() => { /* a usage figure is never worth an error message */ })
    .finally(() => { inflight = null })
  return inflight
}

const refreshSoon = () => {
  clearTimeout(timer)
  timer = setTimeout(refreshCredits, 1200)
}

if (typeof window !== 'undefined') {
  window.addEventListener(CREDITS_CHANGED_EVENT, refreshSoon)
  window.addEventListener('focus', refreshSoon)
}

/** Forget everything, so the next user never sees the last one's balance. */
function resetCredits() {
  current = null
  loaded = false
  listeners.forEach((fn) => fn())
}

const subscribe = (fn) => {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}
const getSnapshot = () => current

export default function useCredits() {
  const { user, isGuest } = useAuth()
  const enabled = !!user && !isGuest
  const credits = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const userId = user?.id ?? null
  useEffect(() => {
    // A different person (sign-out, sign-in as someone else) must never see the
    // previous person's balance, even for the moment before the fetch lands.
    if (owner !== userId) { owner = userId; resetCredits() }
    if (enabled && !loaded) refreshCredits()
  }, [enabled, userId])

  return enabled ? credits : null
}
